import { COPY } from '@/shared/constants/copies';
import {
	ASSIGNMENT_LINK_VARIABLE,
	ASSIGNMENT_MESSAGE_VARIABLES,
	BUILT_IN_MESSAGE_VARIABLES,
	MessageVariable,
} from '@/shared/constants/message-constants';
import { CUSTOM_STAGES } from '@/shared/constants/stage';
import { IQuickReply } from '@/shared/services/forms/forms.types';
import { tokensToEditorHtml } from '@/shared/utils/template-variable-utils';

export const ARCHIVE_QUICK_REPLY_NAME = 'Archive';
export const SHORTLIST_QUICK_REPLY_NAME = 'Shortlist';
export const ASSIGNMENT_QUICK_REPLY_NAME = 'Assignment';

type BuiltInQuickReply = {
	/** Completes "This template will be the default suggestion …" in the editor banner. */
	hint: string;
	/** Purely a stage move, so it can never carry an assignment link. */
	stageOnly: boolean;
	/** Injected on read when the backend omits this template. Omit the field to never inject. */
	fallbackText?: string;
};

/** Built line by line rather than through `parseTextToHtml`, which is a no-op on the server. */
const toEditorParagraphs = (lines: string[], variables: MessageVariable[]): string =>
	lines.map((line) => `<p>${tokensToEditorHtml(line, variables)}</p>`).join('');

const ASSIGNMENT_FALLBACK_TEXT = toEditorParagraphs(
	[
		'Hi {first_name},',
		'',
		`As a next step, please find the assignment link here: ${ASSIGNMENT_LINK_VARIABLE.token}`,
		'',
		'To access the link, please make sure you sign-in with the same email.',
	],
	ASSIGNMENT_MESSAGE_VARIABLES
);

/** The templates the backend ships on every form. Key order is the seed priority — add one here and nowhere else. */
export const BUILT_IN_QUICK_REPLIES: Record<string, BuiltInQuickReply | undefined> = {
	[ARCHIVE_QUICK_REPLY_NAME]: { hint: COPY.messaging.quickReplyHintArchive, stageOnly: true },
	[SHORTLIST_QUICK_REPLY_NAME]: { hint: COPY.messaging.quickReplyHintShortlist, stageOnly: true },
	[ASSIGNMENT_QUICK_REPLY_NAME]: {
		hint: COPY.messaging.quickReplyHintAssignment,
		stageOnly: false,
		fallbackText: ASSIGNMENT_FALLBACK_TEXT,
	},
};

/** Tops up a form created before a built-in shipped; saving the list then persists it for real. */
export const withFallbackQuickReplies = (quickReplies: IQuickReply[] = []): IQuickReply[] => {
	const missing = Object.entries(BUILT_IN_QUICK_REPLIES).flatMap(([name, builtIn]) => {
		const text = builtIn?.fallbackText;
		const alreadyThere = quickReplies.some((reply) => reply.name === name);

		return text === undefined || alreadyThere ? [] : [{ name, text }];
	});

	if (!missing.length) return quickReplies;

	return [
		...quickReplies,
		...missing.map(({ name, text }, index) => ({
			name,
			text,
			priority: quickReplies.length + index,
			isDefault: true,
		})),
	];
};

export const isNonDeletableQuickReply = (name: string): boolean => !!BUILT_IN_QUICK_REPLIES[name];

/** A custom or brand-new template may well be a second assignment template, so it gets the link. */
export const getQuickReplyVariables = (name?: string): MessageVariable[] =>
	name && BUILT_IN_QUICK_REPLIES[name]?.stageOnly
		? BUILT_IN_MESSAGE_VARIABLES
		: ASSIGNMENT_MESSAGE_VARIABLES;

/** The template suggested after a move to `stage`: Archive/Shortlist for the built-in stages, else the stage's own name. */
export const getStageQuickReplyName = (stage: string): string => {
	if (stage === CUSTOM_STAGES.ARCHIVED) return ARCHIVE_QUICK_REPLY_NAME;
	if (stage === CUSTOM_STAGES.SHORTLISTED) return SHORTLIST_QUICK_REPLY_NAME;
	return stage;
};

/** Only the seed for a workspace that has never saved templates; the server value wins once it exists. */
export const WORKSPACE_DEFAULT_QUICK_REPLIES: IQuickReply[] = Object.entries(
	BUILT_IN_QUICK_REPLIES
).map(([name, builtIn], priority) => ({
	name,
	text: builtIn?.fallbackText ?? '',
	priority,
	isDefault: true,
}));
